import React, { FC, useEffect, useMemo, useState } from 'react';
import { useForm, SaveButtonProps } from '@refinedev/antd';
import { Form, Col, Row } from 'antd';
import {
    useTranslate,
    IResourceComponentsProps,
    useNavigation,
    useList,
} from '@refinedev/core';
import { ISertRequest } from 'interfaces/application.interface';
import { FileUploader, Success } from '../../../components/file-uploader';
import {
    assembleString,
    removeObject,
    storeObject,
} from '../../../utils/accessory';
import { useDebounce } from 'react-use';
import hydrateVErrors from '../../../utils/validate';
import isJsonString from '../../../utils/json-check';
import { SaveOutlined } from '@ant-design/icons';
import { InternalLayout } from '../../../layouts/InternalLayout';
import styles from './add.module.scss';
import { AutoComplete, Button, FormItem, Input, Textarea } from 'components/ui';

const INPUT_DELAY_MS = 900;
const STORE_KEY = 'add';

export const ApplicationAdd: FC<IResourceComponentsProps> = () => {
    const t = useTranslate();
    const [fileReady, setFileReady] = useState(false);
    const [val, setVal] = useState<string>('');
    const [debouncedValue, setDebouncedValue] = useState<string>('');
    const { replace } = useNavigation();

    const onAfterAdd = (): void => {
        removeObject(STORE_KEY);
        form?.resetFields();
        setFileReady(false);
    };

    const { formProps, saveButtonProps, form } = useForm<any>({
        onMutationSuccess: ({ data }) => {
            let appId: number = data?.data.id;
            appId && onAfterAdd();
        },
        onMutationError: (error, variables) => {
            storeObject(STORE_KEY, error?.config?.data);

            const validationErrors = hydrateVErrors(error);

            let fields: any[] = [];
            // eslint-disable-next-line array-callback-return
            Object.keys(validationErrors).map((key: string) => {
                if (key === 'certificate.number') {
                    fields.push({
                        name: ['certificate', 'name'],
                        errors: [t('The application field is required.')],
                    });
                } else {
                    fields.push({
                        name: [key],
                        errors: [t(`${validationErrors[key]}`)],
                    });
                }
            });

            form.setFields(fields);
        },
        redirect: false,
        resource: 'applications/attach-ectd',
        successNotification: {
            message: t(
                'notifications.the_download_is_complete_and_wait_for_it_to_be_processed_successfully_check_the_status_of_dossier_processing_after_some_time'
            ),
            type: 'success',
        },
    });

    const handleSuccess = (fileId: Success) => {
        if (fileId) {
            form?.setFieldsValue({ file_id: fileId });
            setFileReady(true);
        } else {
            form?.setFieldsValue({ file_id: null });
            setFileReady(false);
        }
    };

    const initialState = {
        certificate: {
            name: '',
            reg_date: '',
            number: '',
        },
        change_reason: '',
        change_text: '',
        file_id: '',
    };

    useDebounce(
        () => {
            setDebouncedValue(val);
        },
        INPUT_DELAY_MS,
        [val]
    );

    const { data } = useList<ISertRequest>({
        resource: 'dictionary/certificate-request',

        filters: [
            {
                operator: 'eq',
                field: 'query',
                value: debouncedValue,
            },
        ],
    });

    const requestList: any[] | undefined = data?.data;

    const options = useMemo(() => {
        if (!requestList?.length) {
            return [] as { value: string; label: string }[];
        }

        return requestList.map((item: ISertRequest) => {
            const concated = assembleString(
                [
                    'medication.medicationName',
                    'medication.pharmaceuticalForm',
                    'mohApplicationNumber',
                ],
                item
            );

            return {
                ...item,
                value: concated,
                label: concated,
            };
        });
    }, [requestList]);

    useEffect(() => {
        const setFormFields = () => {
            try {
                let formValues: any = localStorage.getItem(STORE_KEY) ?? {};
                let parsedValues = isJsonString(formValues)
                    ? JSON.parse(formValues)
                    : formValues;
                let objKeys = Object.keys(parsedValues);

                for (let key in objKeys) {
                    let name = objKeys[key];
                    let item = parsedValues[name];

                    if (name === 'file_id') {
                        setFileReady(true);
                    }

                    form.setFieldsValue({
                        [name]: item,
                    });
                }
            } catch (e) {
                console.error(e);
            }
        };

        setFormFields();
    }, [form, fileReady]);

    const handleSelect = (value: string, option: any) => {
        const { mohApplicationNumber, mohRegistrationDate } = option;

        form?.setFields([
            {
                name: ['certificate', 'reg_date'],
                value: mohRegistrationDate,
            },
            {
                name: ['certificate', 'number'],
                value: mohApplicationNumber?.trim(),
            },
            {
                name: ['certificate', 'name'],
                errors: [],
            },
        ]);
    };

    const handleChange = (value: string) => {
        setVal(value);
    };

    const onValuesChange = (values: any) => {
        Object.keys(values).forEach(field => {
            const error = form.getFieldError(field);
            if (!error.length) {
                return;
            }

            form.setFields([
                {
                    name: field,
                    errors: [],
                },
            ]);
        });
    };

    const fileLocked = true;

    const formId = 'application-add-form';

    const { variant: _saveVariant, ...saveButtonRest } =
        (saveButtonProps as SaveButtonProps & { variant?: never }) ?? {};

    const baseDisabled =
        typeof saveButtonRest?.disabled === 'boolean'
            ? saveButtonRest.disabled
            : false;

    const disabled = baseDisabled || !fileReady;

    const saveActionProps: Omit<SaveButtonProps, 'variant'> = {
        ...saveButtonRest,
        disabled,
    };

    return (
        <InternalLayout
            onBack={() => replace('/applications')}
            title={t('applications.titles.add_ectd')}
        >
            <Form
                {...formProps}
                id={formId}
                onValuesChange={onValuesChange}
                layout="vertical"
                initialValues={initialState}
            >
                <Row gutter={[16, 20]}>
                    <Col xs={24} md={24}>
                        <FormItem name={['certificate', 'name']}>
                            <AutoComplete
                                label={t('applications.fields.application')}
                                value={val}
                                options={options}
                                onChange={handleChange}
                                onSelect={handleSelect}
                                placeholder={t('applications.fields.search')}
                            />
                        </FormItem>
                    </Col>

                    <FormItem
                        label={t('applications.fields.reg_date')}
                        name={['certificate', 'reg_date']}
                        hidden
                    />

                    <FormItem
                        label={t(
                            'applications.fields.registration_certificate_number'
                        )}
                        name={['certificate', 'number']}
                        hidden
                    />

                    <Col xs={24} md={24}>
                        <FormItem name="change_reason">
                            <Input
                                label={t('applications.fields.change_reason')}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem name="change_text">
                            <Textarea
                                label={t('applications.fields.change_text')}
                            />
                        </FormItem>
                    </Col>

                    <Col xs={24} md={24}>
                        <FormItem
                            label={t('applications.fields.file')}
                            name="file_id"
                            className={styles.formItem}
                        >
                            <FileUploader
                                onSuccess={handleSuccess}
                                onRemove={() => {
                                    setFileReady(false);
                                }}
                                accept=".zip"
                                disabled={fileLocked}
                            />
                        </FormItem>
                    </Col>
                </Row>
            </Form>

            <div className={styles.footerActions}>
                <Button {...saveActionProps} form={formId}>
                    {t('buttons.save')}
                </Button>
            </div>
        </InternalLayout>
    );
};
